import React, { useState, useRef } from "react";
import ReactPlayer from "react-player";
import { useInView } from "react-intersection-observer";
import SmartColour from "../../utils/ColourHandler";
import SmartStyles from "../../utils/StyleHandler";
import useWindowSize from "../../utils/ScreenHelper";
import CTAs from "../global/Ctas";

const Hero = ({ blok }) => {
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(true);
  const [played, setPlayed] = useState(0); // Track progress percentage
  const playerRef = useRef(null);
  const containerRef = useRef(null); // Ref for fullscreen
  const window = useWindowSize();
  const [ref, inView] = useInView({
    threshold: 0,
  });

  if (!blok) return null; // Escape hatch

  const {
    fade,
    controls,
    video,
    video_mobile,
    image,
    theme,
    ctas,
    padding,
    rounded,
    title,
  } = blok;

  const colour = SmartColour(theme);

  const handlePlay = () => {
    setIsVideoReady(true);
  };

  // Update progress bar
  const handleProgress = (state) => {
    setPlayed(state.played);
  };

  // Seek when user clicks on progress bar
  const handleSeek = (e) => {
    const bar = e.target;
    const clickX = e.nativeEvent.offsetX;
    const newSeekTime =
      (clickX / bar.clientWidth) * playerRef.current.getDuration();
    playerRef.current.seekTo(newSeekTime);
  };

  // Toggle fullscreen
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current
        .requestFullscreen()
        .catch((err) => console.error("Fullscreen failed", err));
    } else {
      document.exitFullscreen();
    }
  };

  return (
    <section
      className={`brand-hero relative flex flex-col overflow-hidden ${
        colour.classes && colour.classes
      } ${SmartStyles("padding", padding)} ${SmartStyles("rounded", rounded)}`}
      style={
        image?.filename ? { backgroundImage: `url(${image.filename})` } : null
      }
      ref={ref}
    >
      <div
        className={`${
          video
            ? `aspect-4x5 sm:aspect-21x9`
            : image?.filename
            ? `aspect-square sm:aspect-21x9`
            : `aspect-21x9 sm:aspect-[21/7]`
        } flex flex-col justify-center align-center overflow-hidden`}
      >
        {video && (
          <div
            className={`aspect-4x5 sm:aspect-21x9 scale-[1.1] ${
              isVideoReady ? "opacity-100" : "opacity-0 brand-wait2"
            } transition-opacity duration-500`}
            ref={containerRef}
          >
            <ReactPlayer
              ref={playerRef}
              url={
                window.width < 640
                  ? video_mobile
                    ? video_mobile
                    : video
                  : video
              }
              onPlay={handlePlay}
              onProgress={handleProgress} // Track progress
              playing={playing && inView}
              muted={muted}
              volume={muted ? 0 : 0.8} // Ensures smooth transition between mute and unmute
              controls={false}
              loop={true}
              width="100%"
              height="100%"
              config={{
                youtube: {
                  playerVars: {
                    dnt: true,
                    responsive: true,
                    playsinline: true,
                  },
                },
                vimeo: {
                  playerOptions: {
                    dnt: true,
                    responsive: true,
                    playsinline: true,
                    background: true,
                    loop: true,
                  },
                },
              }}
            />
          </div>
        )}

        {(title || ctas || (controls && video)) && (
          <div
            className={`absolute w-full h-full flex flex-col top-0 right-0 bottom-0 left-0 justify-center items-center pt-6 lg:pt-12`}
          >
            <div
              className={`w-full text-center px-3 ${
                controls && video ? "mt-auto" : "my-auto"
              } flex flex-col gap-6 lg:gap-6`}
            >
              {title && (
                <h1 className="brand-container mx-auto text-center brand-text-hero mb-0">
                  {title}
                </h1>
              )}
              {ctas && (
                <CTAs ctas={ctas} align="center" className="items-center" />
              )}
            </div>
            {controls && video ? (
              <div className="mb-2 md:mb-5 w-full mt-auto">
                <div className="brand-container mx-auto flex flex-row gap-4 items-center">
                  <button
                    onClick={() => setPlaying((prev) => !prev)}
                    className="w-10 h-10 flex flex-col bg-white/20 brand-bg-blur text-white justify-center items-center grow-0 shrink-0 rounded"
                  >
                    <span className="material-icons text-white">
                      {playing ? "pause" : "play_arrow"}
                    </span>
                  </button>
                  <button
                    onClick={() => setMuted((prev) => !prev)}
                    className="w-10 h-10 flex flex-col bg-white/20 brand-bg-blur text-white justify-center items-center grow-0 shrink-0 rounded"
                  >
                    <span className="material-icons text-white">
                      {muted ? "volume_off" : "volume_up"}
                    </span>
                  </button>
                  <div
                    className="bg-white/20 brand-bg-blur w-full h-2 rounded overflow-hidden grow"
                    onClick={handleSeek}
                  >
                    <div
                      className={`${
                        playing ? "brand-grad" : "bg-red"
                      } h-full transition-all`}
                      style={{ width: `${played * 100}%` }}
                    />
                  </div>
                  <button
                    onClick={toggleFullscreen}
                    className="w-10 h-10 flex flex-col bg-white/20 brand-bg-blur text-white justify-center items-center grow-0 shrink-0 rounded"
                  >
                    <span className="material-icons text-white">
                      fullscreen
                    </span>
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        )}
      </div>

      {fade ? (
        <div
          className={`absolute left-0 bottom-0 right-0 h-24 xl:h-48 bg-gradient-to-t from-${
            colour && colour.text
          } from-2%`}
        />
      ) : null}
    </section>
  );
};

export default Hero;
